import { Modal } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useState, type ReactNode } from "react";
import { Button } from "./Button";
import { NARROW } from "./Drawer";
import { STRINGS } from "./strings";

/* A centered dialog for something small: a confirmation, an export, one
 * field. docs/design-standards.md, Shape follows the job: a record is a page,
 * and something tiny gets a centered modal. On a phone it fills the screen,
 * so its buttons are in reach and nothing hides behind the keyboard.
 *
 * The buttons are at the foot: the action, then Cancel. The action's words
 * say what happens, never OK. */
export function Dialog({ opened, title, onClose, children, action, actionKind = "orange", busy, disabled, error }: {
  opened: boolean;
  title: string;
  onClose: () => void;
  children?: ReactNode;
  /** The action's words and what it does. Absent for a dialog that only shows. */
  action?: { label: string; onPress: () => void };
  actionKind?: "orange" | "danger";
  busy?: boolean;
  disabled?: boolean;
  /** The engine's refusal, shown as written above the buttons. */
  error?: string | null;
}) {
  const narrow = useMediaQuery(NARROW, false, { getInitialValueInEffect: false });
  return (
    <Modal opened={opened} onClose={onClose} title={title} centered fullScreen={narrow}
           closeButtonProps={{ "aria-label": STRINGS.close }} classNames={{ title: "ui-dialog-title" }}>
      <div className="ui-dialog-body">{children}</div>
      {error ? <p className="refusal" role="alert">{error}</p> : null}
      <div className="ui-dialog-foot">
        {action ? (
          <Button kind={actionKind} onClick={action.onPress} disabled={Boolean(disabled || busy)} loading={Boolean(busy)}>
            {action.label}
          </Button>
        ) : null}
        <Button kind="link" onClick={onClose}>{action ? STRINGS.cancel : STRINGS.close}</Button>
      </div>
    </Modal>
  );
}

/** A yes-or-cancel question in one line, for an archive or a stage move. */
export type Ask = { title: string; says: string; label: string; danger?: boolean; run: () => Promise<unknown> };

/** One confirmation at a time: `ask` opens it, and the dialog runs the
 *  action, shows the engine's refusal if there is one, and closes on success. */
export function useConfirm() {
  const [asking, setAsking] = useState<Ask | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = () => { setAsking(null); setError(null); setBusy(false); };
  const dialog = (
    <Dialog
      opened={Boolean(asking)} title={asking?.title ?? ""} onClose={close} busy={busy} error={error}
      actionKind={asking?.danger ? "danger" : "orange"}
      {...(asking ? {
        action: {
          label: asking.label,
          onPress: async () => {
            setBusy(true); setError(null);
            try { await asking.run(); close(); }
            catch (e) { setBusy(false); setError(e instanceof Error ? e.message : String(e)); }
          },
        },
      } : {})}
    >
      <p>{asking?.says}</p>
    </Dialog>
  );
  return { ask: (a: Ask) => { setError(null); setAsking(a); }, dialog };
}
